import { useTranslation } from 'react-i18next'
import { ErpOrderItem } from '@ca/common/types'
import Row, { textProps } from '@ui/elements/SectionRow/NestedRow'
import CollapsibleSectionRow from '@ui/elements/SectionRow/CollapsibleSectionRow'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import { IconName } from '@ui/elements/Icon'
import Price from '@/price/Price'
import Gradient from '@ui/effects/Gradient'
import { View } from 'react-native'
import { hex } from '@ui/utils/hex'

import { SectionProps } from '../types'
import cn from '@ui/utils/cn'

type Props = SectionProps

const ReceiptItemTypes = {
  right: 'right',
  left: 'left',
  frame: 'frame',
  service: 'service',
  discount: 'discount',
  other: 'other',
} as const

type ReceiptItemType = (typeof ReceiptItemTypes)[keyof typeof ReceiptItemTypes]

const getReceiptItemType = (item: ErpOrderItem): ReceiptItemType => {
  if (item.price < 0) return 'discount'
  if (item.frl.includes('right')) return 'right'
  if (item.frl.includes('left')) return 'left'
  if (item.frl.includes('frame')) return 'frame'
  if (item.frl.includes('service')) return 'service'
  return 'other'
}

const receiptItemIcon: Record<ReceiptItemType, IconName> = {
  right: 'letter-r',
  left: 'letter-l',
  frame: 'eye-glass2',
  service: 'plus',
  discount: 'discount',
  other: 'point',
}

export default function ReceiptSection({ data }: Props) {
  const { t } = useTranslation(['ui', 'common', 'order'])
  const header = 'order:details.sections.receipt.header'
  const label = 'order:details.receipt'

  const total = data.items.reduce((acc, item) => acc + item.price, 0)

  return (
    <CollapsibleSectionRow
      icon="receipt-2"
      title={t(`${header}.title`)}
      desc={t(`${header}.desc`)}
    >
      {data.items.map((item, index) => {
        const type = getReceiptItemType(item)

        return (
          <Row
            key={index}
            icon={receiptItemIcon[type]}
            title={t(`${label}.${type}`)}
            desc={item.description.replaceAll('  ', '').trim()}
            body={
              <Typography
                {...textProps}
                color={
                  type === 'discount' ? colors.green[600] : colors.red[600]
                }
                className={cn(
                  textProps.className,
                  'flex flex-row items-end justify-end'
                )}
              >
                <Price value={item.price} showZero />
              </Typography>
            }
          />
        )
      })}
      <View>
        <Gradient
          colors={[hex(colors.gray[300], 0.2), colors.white0]}
          start={[0, 0.5]}
          end={[1, 0.5]}
        />
        <Row
          last
          icon="shopping-bag-filled"
          title={t(`${label}.total`)}
          body={
            <Typography variant="SMB" className="shrink-0" numberOfLines={1}>
              <Price value={total} showZero />
            </Typography>
          }
        />
      </View>
    </CollapsibleSectionRow>
  )
}
